import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import {
  getAccessToken,
  getRefreshToken,
  setTokens,
  clearTokens,
  isTokenExpired,
} from '@crm/utils';
import type { ApiError } from './types/common';

/** API client configuration */
export interface ApiClientConfig {
  baseURL: string;
  tenantId?: string;
  onAuthError?: () => void;
}

/** Create an authenticated API client */
export function createApiClient(config: ApiClientConfig): AxiosInstance {
  const client = axios.create({
    baseURL: config.baseURL,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  // Request interceptor - add auth token and tenant header
  client.interceptors.request.use(
    async (request: InternalAxiosRequestConfig) => {
      const token = getAccessToken();

      if (token) {
        // Check if token is expired and refresh if needed
        if (isTokenExpired(token)) {
          const refreshed = await refreshAccessToken(config.baseURL);
          if (refreshed) {
            request.headers.Authorization = `Bearer ${getAccessToken()}`;
          } else {
            clearTokens();
            config.onAuthError?.();
            return Promise.reject(new Error('Session expired'));
          }
        } else {
          request.headers.Authorization = `Bearer ${token}`;
        }
      }

      // Add tenant header if configured
      if (config.tenantId) {
        request.headers['X-Tenant-ID'] = config.tenantId;
      }

      return request;
    },
    (error) => Promise.reject(error)
  );

  // Response interceptor - handle errors
  client.interceptors.response.use(
    (response) => response,
    async (error: AxiosError<ApiError>) => {
      const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

      // Handle 401 - try token refresh
      if (error.response?.status === 401 && !originalRequest._retry) {
        originalRequest._retry = true;

        const refreshed = await refreshAccessToken(config.baseURL);
        if (refreshed) {
          originalRequest.headers.Authorization = `Bearer ${getAccessToken()}`;
          return client(originalRequest);
        }

        clearTokens();
        config.onAuthError?.();
      }

      // Handle 403 - might be invalid/expired token, clear and redirect
      if (error.response?.status === 403) {
        const token = getAccessToken();
        // If we have a token but still getting 403, it might be invalid
        if (token && isTokenExpired(token)) {
          clearTokens();
          config.onAuthError?.();
        }
      }

      return Promise.reject(error);
    }
  );

  return client;
}

/** Attempt to refresh the access token */
async function refreshAccessToken(baseURL: string): Promise<boolean> {
  const refreshToken = getRefreshToken();
  if (!refreshToken) return false;

  try {
    const response = await axios.post(`${baseURL}/api/v1/auth/token/refresh/`, {
      refresh: refreshToken,
    });

    const { access, refresh } = response.data;
    setTokens(access, refresh || refreshToken);
    return true;
  } catch {
    return false;
  }
}

/** Default API client singleton (to be initialized by app) */
let defaultClient: AxiosInstance | null = null;
let clientConfig: ApiClientConfig | null = null;

export function initializeApiClient(config: ApiClientConfig): AxiosInstance {
  defaultClient = createApiClient(config);
  clientConfig = config;
  return defaultClient;
}

export function getApiClient(): AxiosInstance {
  if (!defaultClient) {
    throw new Error('API client not initialized. Call initializeApiClient first.');
  }
  return defaultClient;
}

/** Get the API base URL (for WebSocket connections, etc.) */
export function getApiBaseUrl(): string | null {
  return clientConfig?.baseURL ?? null;
}
